import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import { useStatics } from '@/composables/useStatics';

export function useDashboardPDF(data, companyConfig) {
    const { brandName,brandWebSite } = useStatics();

    return new Promise((resolve) => {
        const doPrintAsync = async () => {
            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
            const margin = 15;
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            let currentY = 15;

            // --- 1. PROCESAMIENTO DE LOGO (ASÍNCRONO) ---
            const logoUrl = companyConfig?.logo_url;
            if (logoUrl) {
                try {
                    const imgBase64 = await new Promise((res) => {
                        const img = new Image();
                        img.crossOrigin = 'Anonymous';
                        img.onload = () => {
                            const canvas = document.createElement('canvas');
                            canvas.width = img.width; canvas.height = img.height;
                            const ctx = canvas.getContext('2d');
                            ctx.drawImage(img, 0, 0);
                            res(canvas.toDataURL('image/png'));
                        };
                        img.onerror = () => res(null);
                        img.src = logoUrl;
                    });
                    if (imgBase64) {
                        doc.addImage(imgBase64, 'PNG', margin, currentY, 30, 15);
                    }
                } catch (e) { console.warn("No se pudo cargar el logo"); }
            }
            const formatNum = (num, decimals = 0) => {
                return new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: decimals,
                    maximumFractionDigits: decimals
                }).format(num);
            };
            // --- 2. ENCABEZADO DETALLADO ---
            doc.setFontSize(14).setFont(undefined, 'bold').setTextColor(40);
            doc.text(companyConfig?.emitter_name || "EMPRESA CLIENTE", margin + 35, currentY + 5);

            doc.setFontSize(8).setFont(undefined, 'normal').setTextColor(100);
            doc.text(`RUC: ${companyConfig?.emitter_ruc || '---'}`, margin + 35, currentY + 10);
            doc.text(`Dirección: ${companyConfig?.emitter_address || 'Sin dirección registrada'}`, margin + 35, currentY + 14);

            // Título de Reporte (Derecha)
            doc.setFontSize(12).setFont(undefined, 'bold').setTextColor(255, 128, 0);
            doc.text("REPORTE DE RENDIMIENTO DIARIO", pageWidth - margin, currentY + 5, { align: 'right' });

            doc.setFontSize(8).setFont(undefined, 'normal').setTextColor(100);
            doc.text(`Fecha Impresión: ${moment().format('DD/MM/YYYY hh:mm A')}`, pageWidth - margin, currentY + 10, { align: 'right' });
            doc.text(`Impreso por: ${localStorage.getItem('userName') || 'Administrador'}`, pageWidth - margin, currentY + 14, { align: 'right' });

            doc.setDrawColor(255, 128, 0).setLineWidth(0.5).line(margin, currentY + 18, pageWidth - margin, currentY + 18);
            currentY += 30;

            // --- 3. GRÁFICOS ---
            const drawCrackChart = (title, x, y, width, height, type) => {
                const branchEntries = Object.entries(data.branchTotals);
                if (branchEntries.length === 0) return;
                doc.setFontSize(10).setFont(undefined, 'bold').setTextColor(40).text(title, x, y - 8);

                const label1 = type === 'tasks' ? 'Asignadas' : 'Proyectado';
                const label2 = type === 'tasks' ? 'Completadas' : 'Recuperado';
                doc.setFillColor(255, 128, 0).rect(x, y - 5, 2.5, 2.5, 'F');
                doc.setFontSize(7).setTextColor(80).text(label1, x + 4, y - 3);
                doc.setFillColor(129, 193, 75).rect(x + 20, y - 5, 2.5, 2.5, 'F');
                doc.text(label2, x + 24, y - 3);

                doc.setDrawColor(230).setLineWidth(0.1);
                for (let i = 0; i <= 4; i++) {
                    let yLine = y + height - (i * (height / 4));
                    doc.line(x, yLine, x + width, yLine);
                }

                const gap = width / branchEntries.length;
                const barW = gap / 3.8;
                const maxVal = Math.max(...branchEntries.map(b => type === 'tasks' ? b[1].assigned : b[1].to_collect)) * 1.2;

                branchEntries.forEach(([name, totals], i) => {
                    const xBase = x + (i * gap) + (barW);
                    const v1 = type === 'tasks' ? totals.assigned : totals.to_collect;
                    const v2 = type === 'tasks' ? totals.completed : totals.collected;
                    const h1 = (v1 / maxVal) * height;
                    const h2 = (v2 / maxVal) * height;

                    doc.setFillColor(255, 128, 0).rect(xBase, y + height - h1, barW, h1, 'F');
                    doc.setFontSize(6).setTextColor(40).text(type === 'tasks' ? `${v1}` : `$${Math.round(v1)}`, xBase + (barW/2), y + height - h1 - 1, { align: 'center' });
                    doc.setFillColor(129, 193, 75).rect(xBase + barW, y + height - h2, barW, h2, 'F');
                    doc.text(type === 'tasks' ? `${v2}` : `$${Math.round(v2)}`, xBase + barW + (barW/2), y + height - h2 - 1, { align: 'center' });
                    doc.setFontSize(7).setTextColor(60).text(name.split(' ')[0], xBase + barW, y + height + 5, { align: 'center' });
                });
            };

            drawCrackChart("PRODUCTIVIDAD DE CLIENTES (TAREAS)", margin, currentY, 110, 40, 'tasks');
            drawCrackChart("RECUPERACIÓN DE EFECTIVO (MONTO $)", margin + 125, currentY, 110, 40, 'money');
            currentY += 60;

            // --- 4. TABLAS POR AGENCIA CON FORMATO ---
            for (const [branchName, totals] of Object.entries(data.branchTotals)) {
                if (currentY > 170) { doc.addPage(); currentY = 25; }

                doc.autoTable({
                    startY: currentY,
                    head: [[`AGENCIA: ${branchName.toUpperCase()}`, 'Asig.', 'Real.', '% Ok', 'Meta $', 'Cobro $', '% $']],
                    body: [
                        ...data.branchPerformanceData.filter(u => u.branch_name === branchName).map(u => [
                            u.employee_name,
                            formatNum(u.total_assigned, 0),
                            formatNum(u.total_completed, 0),
                            `${u.compliance_rate}%`,
                            `$ ${formatNum(u.assigned_amount, 2)}`,
                            `$ ${formatNum(u.realized_amount, 2)}`,
                            `${u.collection_rate}%`
                        ]),
                        // FILA DE TOTAL AGENCIA CON FORMATO
                        [
                            { content: 'TOTAL AGENCIA', styles: { fontStyle: 'bold', fillColor: [21, 50, 67], textColor: 255 } },
                            { content: formatNum(totals.assigned, 0), styles: { fontStyle: 'bold', fillColor:  [21, 50, 67], textColor: 255 } },
                            { content: formatNum(totals.completed, 0), styles: { fontStyle: 'bold', fillColor:  [21, 50, 67], textColor: 255 } },
                            { content: `${((totals.completed / totals.assigned) * 100).toFixed(2)}%`, styles: { fontStyle: 'bold', fillColor:  [21, 50, 67], textColor: 255 } },
                            { content: `$ ${formatNum(totals.to_collect, 2)}`, styles: { fontStyle: 'bold', fillColor:  [21, 50, 67], textColor: 255 } },
                            { content: `$ ${formatNum(totals.collected, 2)}`, styles: { fontStyle: 'bold', fillColor:  [21, 50, 67], textColor: 255 } },
                            { content: `${((totals.collected / totals.to_collect) * 100).toFixed(2)}%`, styles: { fontStyle: 'bold', fillColor:  [21, 50, 67], textColor: 255 } }
                        ]
                    ],
                    theme: 'striped',
                    headStyles: { fillColor: [255, 128, 0], textColor: 255, fontSize: 8 },
                    styles: { fontSize: 7, halign: 'center' },
                    columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
                    margin: { left: margin, right: margin }
                });
                currentY = doc.lastAutoTable.finalY + 10;
            }

            // --- 5. GRAN TOTAL CON FORMATO ---
            if (currentY > 185) { doc.addPage(); currentY = 25; }
            doc.autoTable({
                startY: currentY,
                head: [['RESUMEN GENERAL (GRAN TOTAL)', 'Tareas Asig.', 'Tareas Real.', '% Ok', 'Meta Total $', 'Cobro Total $', '% Cobro']],
                body: [[
                    'CONSOLIDADO '+companyConfig?.emitter_name,
                    formatNum(data.grandTotal.assigned, 0),
                    formatNum(data.grandTotal.completed, 0),
                    `${data.grandTotal.taskPercent}%`,
                    `$ ${formatNum(data.grandTotal.to_collect, 2)}`,
                    `$ ${formatNum(data.grandTotal.collected, 2)}`,
                    `${data.grandTotal.percent}%`
                ]],
                theme: 'grid',
                headStyles: { fillColor:  [255, 128, 0] }, // Un gris oscuro para el Gran Total se ve muy elegante
                styles: { fontSize: 8, halign: 'center', fontStyle: 'bold' },
                margin: { left: margin, right: margin }
            });

            // --- 6. FOOTER (Tu Branding) ---
            const totalPages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFontSize(6).setTextColor(150);

                const footerText = `Servicio provisto por: ${brandName}  |  `;
                const textWidth = doc.getTextWidth(footerText);
                doc.text(footerText, margin, pageHeight - 10);

                // Link interactivo
                doc.setTextColor(0, 114, 198);
                doc.text(brandWebSite, margin + textWidth, pageHeight - 10);
                doc.link(margin + textWidth, pageHeight - 13, doc.getTextWidth(brandWebSite), 5, { url: `https://${brandWebSite}` });

                doc.setFontSize(8).setTextColor(0);
                // Numeración
                doc.setTextColor(0);
                doc.text(`Página ${i} de ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
            }

            doc.save(`Dashboard_${companyConfig?.emitter_name || 'Reporte'}_${moment().format('YYYYMMDD')}.pdf`);
            resolve();
        };
        doPrintAsync();
    });
}